import React, { CSSProperties } from 'react';
import { Box, Button, Typography } from '@mui/material';
import LinkedIn from '@mui/icons-material/LinkedIn';
import PersonOutline from '@mui/icons-material/PersonOutline';
import { useConfirm } from 'material-ui-confirm';
import { ContactSchema } from '../../../../definitions/schemas';
import { getDisplayName } from '../../../../lib/DisplayName';
import RoomsInterface from '../../../../graphql/Rooms/RoomsInterface';
import SnackbarStore from '../../../../stores/observables/SnackbarStore';
import { Events, Properties, Referrers } from '../../../../enums/Analytics';
import Analytics, { EventProperty } from '../../../../tools/Analytics';
import { isVanityUrl } from '../../../../lib/helper/RoomHelper';
import AnonymousIdentityGuard from '../../../Room/View/AnonymousIdentityGuard';
import { isVendorMode } from '../../../../lib/Vendor';
import { useI18n } from '../../../../lib/hook/useI18n';

export interface ContactInformationProps {
  buttonsStyles?: CSSProperties;
  actionable?: boolean;
  contact: ContactSchema;
  contentId: string;
  roomId?: string;
  textStyles?: CSSProperties;
  trackingProperties?: EventProperty;
  userContactId?: string;
}

const ContactInformation: React.FC<ContactInformationProps> = (
  props: ContactInformationProps
) => {
  const {
    actionable = false,
    buttonsStyles,
    contact,
    contentId,
    roomId,
    textStyles,
    trackingProperties = {},
    userContactId,
  } = props;
  const confirm = useConfirm();
  const { t } = useI18n();

  const onContactClick = () => {
    if (!(actionable && contact?.id && roomId)) {
      return;
    }

    confirm({
      title: t('references.modal.title'),
      description: t('references.modal.body'),
      cancellationButtonProps: {
        color: 'inherit',
      },
      cancellationText: t('references.modal.cancel'),
      confirmationText: t('references.modal.yes'),
      confirmationButtonProps: {
        color: 'secondary',
        style: buttonsStyles,
        variant: 'contained',
      },
      dialogProps: {
        maxWidth: 'sm',
      },
    }).then(
      async () => {
        try {
          await new RoomsInterface(userContactId).reachOutContentInfluencer({
            contentId,
            roomId,
          });
          SnackbarStore.show(t('references.modal.success'));
          Analytics.track(Events.CONTACT_REQUESTED, {
            ...trackingProperties,
            [Properties.VANITY_URL]: isVanityUrl(window.location.href),
          });
        } catch (e) {
          SnackbarStore.show(e as string);
        }
      },
      () => {
        /* do nothing */
      }
    );
  };

  const renderRequestContact = () => {
    if (isVendorMode() || !contact.contactable) {
      return;
    }

    return (
      <Box whiteSpace="nowrap">
        <AnonymousIdentityGuard
          referrer={Referrers.DEAL_ROOMS.ACTIVITY.REQUEST_CONTACT}
        >
          <Button
            color="secondary"
            disabled={!actionable}
            onClick={onContactClick}
            startIcon={<PersonOutline style={textStyles} />}
            style={{ textTransform: 'none', ...textStyles }}
          >
            {t('references.request_contact')}
          </Button>
        </AnonymousIdentityGuard>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }}>
      <Box flex="1" pr={{ lg: 2 }}>
        <Box display="flex" flexDirection="row">
          <Box pr={1} width={80}>
            <Typography component="span" variant="body2">
              <Box component="strong">{t('references.customer')}</Box>
            </Typography>
          </Box>
          <Box flex="1">
            <Typography component="span" variant="body2">
              {getDisplayName(contact)}
            </Typography>
          </Box>
        </Box>
        {contact.company && (
          <Box display="flex" flexDirection="row">
            <Box pr={1} width={80}>
              <Typography component="span" variant="body2">
                <Box component="strong">{t('references.company')}</Box>
              </Typography>
            </Box>
            <Box flex="1">
              <Typography component="span" variant="body2">
                {contact.company.name}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection={{ md: 'row' }}
        flex="0"
        pt={{ xs: 1, lg: 0 }}
      >
        {contact.linkedin && (
          <Box pr={3} whiteSpace="nowrap">
            <Button
              color="secondary"
              href={contact.linkedin}
              startIcon={<LinkedIn style={textStyles} />}
              style={{ textTransform: 'none', ...textStyles }}
              target="_blank"
            >
              LinkedIn
            </Button>
          </Box>
        )}
        {renderRequestContact()}
      </Box>
    </Box>
  );
};

export default ContactInformation;
